import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ImageViewerModal.css';

interface ImageViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string | null;
  imageTitle?: string;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  imageTitle
}) => {
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen || !imageUrl) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = imageTitle || 'image';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyUrl = async () => {
    if (!imageUrl) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(imageUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = imageUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopyState('copied');
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => setCopyState('idle'), 2000);
    } catch (error) {
      setCopyState('error');
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => setCopyState('idle'), 2000);
    }
  };

  return (
    <div className="image-viewer-backdrop" onClick={handleBackdropClick}>
      <div className="image-viewer-container">
        <div className="image-viewer-header">
          <h3>{imageTitle || t('modals.imageViewer.title')}</h3>
          <div className="image-viewer-actions">
            <button
              className="image-viewer-button"
              onClick={handleDownload}
              title={t('modals.imageViewer.download')}
            >
              ⬇ {t('modals.imageViewer.download')}
            </button>
            <button
              className="image-viewer-button"
              onClick={handleCopyUrl}
              title={t('modals.imageViewer.copyUrl')}
            >
              {copyState === 'copied' ? t('modals.imageViewer.copied') : t('modals.imageViewer.copyUrl')}
            </button>
            <button
              className="image-viewer-close"
              onClick={onClose}
              title={t('modals.imageViewer.close')}
            >
              ×
            </button>
          </div>
        </div>
        <div className="image-viewer-content">
          <img
            src={imageUrl}
            alt={imageTitle || t('modals.imageViewer.fullSizeImage')}
            className="image-viewer-image"
          />
        </div>
      </div>
    </div>
  );
};

export default ImageViewerModal;
